// iter-81al — AI Bulk Document Uploader for the Staff File.
// Drop 1..N PDF/image files. If a PDF has multiple pages we call the analyze
// endpoint with split=true so each page comes back as its own row. Each row
// shows the AI's guess (checklist item, staff, dates, N/A flag, review-needed)
// which you can correct before hitting "Save all". Saving posts each row as
// /staff/documents so the checklist auto-syncs.
import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Upload, Loader2, Sparkles, CheckCircle2, AlertTriangle, X, FilePlus, ScanLine, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

// helper: convert base64 string back to Blob for re-upload of split pages
function b64ToBlob(b64, mime) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

const emptyRow = () => ({
  id: crypto.randomUUID(),
  filename: "", file: null, contentType: "application/pdf",
  page_num: 1,
  title: "", staff_name: "", checklist_key: "",
  completion_date: "", expires_on: "",
  has_expiration: true, review_needed: false, confidence: "medium",
  reasoning: "", analyzing: true, error: "", saved: false,
});

export default function AIBulkUploader({
  open, onOpenChange, template, defaultStaffName = "", onDone,
}) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (open) { setRows([]); setSavedCount(0); }
  }, [open]);

  const analyzeOne = async (file, seedRow) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    // Ask backend to split multi-page PDFs
    if (file.type === "application/pdf") fd.append("split", "true");
    try {
      const res = await api.post("/staff/documents/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data || {};
      if (Array.isArray(data.results)) {
        const isSplit = data.results.length > 1;
        return data.results.map((r, idx) => {
          // For split PDFs, we keep the base64 so each page can be saved separately.
          const pageFile = isSplit
            ? b64ToBlob(data.file_b64, data.content_type)
            : file;
          return {
            ...seedRow,
            id: crypto.randomUUID(),
            filename: isSplit ? `${file.name} · page ${r.page_num}` : file.name,
            file: pageFile,
            contentType: data.content_type || file.type,
            page_num: r.page_num || (idx + 1),
            title: r.suggested_title || file.name.replace(/\.[a-z0-9]+$/i, ""),
            staff_name: r.staff_name || defaultStaffName || "",
            checklist_key: r.checklist_key || "",
            completion_date: r.completion_date || "",
            expires_on: r.expiration_date || "",
            has_expiration: r.has_expiration !== false,
            review_needed: !!r.review_needed,
            confidence: r.confidence || "medium",
            reasoning: r.reasoning || "",
            analyzing: false, error: "", saved: false,
          };
        });
      }
      // Fallback: non-split single-page response
      return [{
        ...seedRow,
        filename: file.name,
        file, contentType: file.type,
        page_num: 1,
        title: data.suggested_title || file.name.replace(/\.[a-z0-9]+$/i, ""),
        staff_name: data.staff_name || defaultStaffName || "",
        checklist_key: data.checklist_key || "",
        completion_date: data.completion_date || "",
        expires_on: data.expiration_date || "",
        has_expiration: data.has_expiration !== false,
        review_needed: !!data.review_needed,
        confidence: data.confidence || "medium",
        reasoning: data.reasoning || "",
        analyzing: false, error: "", saved: false,
      }];
    } catch (err) {
      return [{
        ...seedRow,
        filename: file.name,
        file, contentType: file.type,
        analyzing: false,
        error: formatApiError(err),
      }];
    }
  };

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(f =>
      /^(application\/pdf|image\/(png|jpe?g|webp|heic|heif))$/i.test(f.type),
    );
    if (!list.length) {
      toast.error("Only PDFs and images are supported.");
      return;
    }
    // Insert placeholder rows first so the UI is responsive
    const placeholders = list.map(f => ({
      ...emptyRow(), filename: f.name, contentType: f.type, analyzing: true,
    }));
    setRows(prev => [...prev, ...placeholders]);
    for (const [i, f] of list.entries()) {
      const seed = placeholders[i];
      const newRows = await analyzeOne(f, seed);
      setRows(prev => {
        // Replace the placeholder with the new row(s)
        const out = [];
        for (const r of prev) {
          if (r.id === seed.id) out.push(...newRows);
          else out.push(r);
        }
        return out;
      });
    }
  };

  const onFileInput = (e) => {
    addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const onDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer?.files);
  };

  const updateRow = (id, patch) =>
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id) => setRows(prev => prev.filter(r => r.id !== id));

  const saveAll = async () => {
    const toSave = rows.filter(r => !r.saved && !r.error && !r.analyzing);
    if (!toSave.length) { toast.error("Nothing to save."); return; }
    setBusy(true);
    let savedNow = 0;
    for (const r of toSave) {
      try {
        if (!r.title.trim()) {
          updateRow(r.id, { error: "Title is required" }); continue;
        }
        if (!r.staff_name.trim()) {
          updateRow(r.id, { error: "Staff name is required" }); continue;
        }
        const fd = new FormData();
        fd.append("file", r.file, r.filename.replace(/ · page.*/, ""));
        fd.append("title", r.title.trim());
        fd.append("category", categoryForKey(r.checklist_key));
        fd.append("staff_name", r.staff_name.trim());
        if (r.checklist_key) fd.append("checklist_key", r.checklist_key);
        if (r.completion_date) fd.append("completion_date", r.completion_date);
        if (r.expires_on && r.has_expiration) fd.append("expires_on", r.expires_on);
        fd.append("has_expiration", r.has_expiration ? "true" : "false");
        fd.append("review_needed", r.review_needed ? "true" : "false");
        if (r.confidence) fd.append("ai_confidence", r.confidence);
        await api.post("/staff/documents", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        updateRow(r.id, { saved: true, error: "" });
        savedNow += 1;
      } catch (err) {
        updateRow(r.id, { error: formatApiError(err) });
      }
    }
    setBusy(false);
    setSavedCount(c => c + savedNow);
    if (savedNow > 0) {
      toast.success(`Saved ${savedNow} document${savedNow > 1 ? "s" : ""} · checklist auto-synced`);
      onDone && onDone();
    }
  };

  const totalPending = rows.filter(r => !r.saved && !r.error && !r.analyzing).length;
  const anyAnalyzing = rows.some(r => r.analyzing);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="ai-bulk-uploader">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-emerald-700" />
            AI document uploader
          </DialogTitle>
          <DialogDescription>
            Drop as many PDFs and photos as you want. The AI reads each document, extracts the
            employee name, dates, and category — and auto-fills the checklist. Review anything
            flagged before saving.
          </DialogDescription>
        </DialogHeader>

        {/* Dropzone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="mt-2 rounded-2xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 p-6 text-center"
          data-testid="ai-bulk-dropzone"
        >
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-2">
            <Upload className="w-6 h-6" />
          </div>
          <div className="text-sm text-stone-700 mb-2">
            Drop files here, or
            <button
              type="button"
              className="ml-1 text-emerald-700 font-semibold underline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="ai-bulk-choose"
            >choose files</button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="application/pdf,image/*"
              className="hidden"
              onChange={onFileInput}
              data-testid="ai-bulk-input"
            />
          </div>
          <div className="text-[11px] text-stone-500">
            PDFs (multi-page auto-split) · JPG · PNG · WEBP · HEIC · max 8&nbsp;MB each
          </div>
        </div>

        {/* Rows */}
        {rows.length > 0 && (
          <div className="mt-4 space-y-3" data-testid="ai-bulk-rows">
            {rows.map((r) => (
              <RowEditor
                key={r.id}
                r={r}
                template={template}
                onChange={(patch) => updateRow(r.id, patch)}
                onRemove={() => removeRow(r.id)}
              />
            ))}
          </div>
        )}

        <DialogFooter className="pt-3 flex-wrap gap-2 justify-between">
          <div className="text-xs text-stone-600">
            {savedCount > 0 && <span className="text-emerald-700 font-semibold">{savedCount} saved · </span>}
            {totalPending} pending {anyAnalyzing && <>· <Loader2 className="inline w-3 h-3 animate-spin" /> analyzing</>}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">Close</Button>
            <Button
              type="button"
              disabled={busy || !totalPending || anyAnalyzing}
              onClick={saveAll}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-xl"
              data-testid="ai-bulk-save-all"
            >
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              <CheckCircle2 className="w-4 h-4 mr-1" />
              Save all &amp; apply to checklist
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RowEditor({ r, template, onChange, onRemove }) {
  const items = template?.items || [];
  const confidenceBadge = useMemo(() => {
    const map = {
      high:   "bg-emerald-100 text-emerald-800 border-emerald-200",
      medium: "bg-amber-50 text-amber-800 border-amber-200",
      low:    "bg-red-50 text-red-800 border-red-200",
    };
    return map[r.confidence] || map.medium;
  }, [r.confidence]);

  if (r.analyzing) {
    return (
      <div className="rounded-xl border border-stone-200 bg-white p-4 flex items-center gap-3" data-testid="ai-row-analyzing">
        <Loader2 className="w-4 h-4 animate-spin text-emerald-700" />
        <div className="text-sm text-stone-700 flex-1 truncate">Reading <b>{r.filename}</b>…</div>
      </div>
    );
  }

  if (r.saved) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center gap-3" data-testid="ai-row-saved">
        <CheckCircle2 className="w-4 h-4 text-emerald-700" />
        <div className="text-sm text-emerald-900 flex-1 truncate">
          Saved: <b>{r.title}</b> · {r.staff_name} · applied to checklist
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${
      r.review_needed ? "border-amber-300 bg-amber-50/40" : "border-stone-200 bg-white"
    }`} data-testid="ai-row">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-[13px] font-semibold text-stone-900 flex items-center gap-2 flex-1 min-w-0">
          <ScanLine className="w-4 h-4 text-emerald-700 shrink-0" />
          <span className="truncate">{r.filename}</span>
        </div>
        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${confidenceBadge}`}>
          {r.confidence} confidence
        </span>
        {r.review_needed && (
          <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-200 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Review needed
          </span>
        )}
        <button type="button" onClick={onRemove} className="text-stone-400 hover:text-red-600" data-testid="ai-row-remove">
          <X className="w-4 h-4" />
        </button>
      </div>

      {r.reasoning && (
        <div className="text-[11px] text-stone-500 italic">"{r.reasoning}"</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Employee name</Label>
          <Input
            value={r.staff_name}
            onChange={(e) => onChange({ staff_name: e.target.value })}
            className="mt-1 h-10" placeholder="Full name"
            data-testid="ai-row-staff-name"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Document title</Label>
          <Input
            value={r.title}
            onChange={(e) => onChange({ title: e.target.value })}
            className="mt-1 h-10"
            data-testid="ai-row-title"
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-stone-500">Checklist item</Label>
          <select
            value={r.checklist_key}
            onChange={(e) => onChange({ checklist_key: e.target.value })}
            className="mt-1 h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm"
            data-testid="ai-row-checklist"
          >
            <option value="">— none —</option>
            {items.map(it => (
              <option key={it.key} value={it.key}>{it.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Completion</Label>
            <Input
              type="date" value={r.completion_date}
              onChange={(e) => onChange({ completion_date: e.target.value })}
              className="mt-1 h-10"
              data-testid="ai-row-completion"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Expires</Label>
            <Input
              type="date" value={r.has_expiration ? r.expires_on : ""}
              disabled={!r.has_expiration}
              onChange={(e) => onChange({ expires_on: e.target.value })}
              className="mt-1 h-10 disabled:bg-stone-100 disabled:text-stone-400"
              data-testid="ai-row-expires"
            />
          </div>
        </div>
        <div className="md:col-span-2 flex items-center gap-2">
          <Checkbox
            id={`na-${r.id}`}
            checked={!r.has_expiration}
            onCheckedChange={(v) => onChange({ has_expiration: !v, expires_on: v ? "" : r.expires_on })}
            data-testid="ai-row-na"
          />
          <label htmlFor={`na-${r.id}`} className="text-xs text-stone-700">
            No expiration date (this document is a one-time record — mark as <b>N/A</b>)
          </label>
        </div>
      </div>

      {r.error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1 flex items-center justify-between gap-2" data-testid="ai-row-error">
          <span><b>Error:</b> {r.error}</span>
          <button type="button" onClick={() => onChange({ error: "" })} className="text-red-500 hover:text-red-900">
            <RotateCcw className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

// Best-effort category derived from checklist_key so the doc lands under the
// right bucket even when the row picker is bypassed.
function categoryForKey(key) {
  const map = {
    education:                "education",
    background_check:         "background_check",
    tb_screening:             "medical",
    tb_test:                  "medical",
    mmr_immunization:         "medical",
    tdap_immunization:        "medical",
    cpr:                      "training",
    first_aid:                "training",
    communicable_disease:     "training",
    jfs_01307_orientation:    "orientation",
    child_abuse:              "training",
    ohio_approach_quality:    "training",
    ohio_child_development:   "training",
    professional_development: "training",
    sutq_topic_1:             "training",
    sutq_topic_2:             "training",
    admin_rules_review:       "training",
    admin_orientation:        "orientation",
  };
  return map[key] || "other";
}

export { AIBulkUploader };
